import { useEffect, useState, useMemo } from "react";
import { Loader2, Save, Trash2, Search, X, AlertCircle, Lock, Unlock } from "lucide-react";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";

export default function ClassificationPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | operational | non_operational

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/category-classification");
      setData(data);
    } catch (e) {
      logError("classification load failed", e);
      toast.error("Could not load classification");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (cat, currentOp) => {
    setBusy(cat);
    try {
      await api.post("/admin/category-classification", { category: cat, is_operational: !currentOp });
      toast.success(`${cat} → ${!currentOp ? "Operational" : "Non-Op"}`);
      await load();
    } catch (e) {
      logError("toggle failed", e);
      toast.error("Update failed");
    } finally { setBusy(null); }
  };

  const reset = async (cat) => {
    setBusy(cat);
    try {
      await api.delete(`/admin/category-classification/${encodeURIComponent(cat)}`);
      toast.success(`${cat} reverted to default`);
      await load();
    } catch (e) {
      logError("reset failed", e);
      toast.error("Reset failed");
    } finally { setBusy(null); }
  };

  const filteredRows = useMemo(() => {
    if (!data) return [];
    let arr = data.categories;
    if (filter === "operational") arr = arr.filter((c) => c.is_operational);
    else if (filter === "non_operational") arr = arr.filter((c) => !c.is_operational);
    const term = q.trim().toLowerCase();
    if (term) arr = arr.filter((c) => c.category.toLowerCase().includes(term));
    return arr;
  }, [data, q, filter]);

  const counts = useMemo(() => {
    if (!data) return { all: 0, op: 0, nop: 0 };
    return data.categories.reduce((m, c) => {
      m.all++;
      if (c.is_operational) m.op++;
      else m.nop++;
      return m;
    }, { all: 0, op: 0, nop: 0 });
  }, [data]);

  if (loading || !data) return (
    <div className="py-16 text-center text-zinc-500">
      <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-classification">
      <header>
        <h2 className="font-display font-black text-2xl tracking-tight">Expense Classification</h2>
        <p className="text-[12px] text-zinc-500 mt-0.5">
          Mark each expense head as <span className="font-bold text-emerald-700">Operational</span> (BD/ops can control)
          or <span className="font-bold text-zinc-700">Non-Operational</span> (out of their control · OTA commission, finance cost, depreciation, etc.).
          Flag stored in MongoDB · used by every Expense Tracker filter.
        </p>
      </header>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="classification-filter">
          <button onClick={() => setFilter("all")} data-testid="classification-filter-all"
                  className={`text-[11px] px-2.5 py-1 rounded border ${filter === "all" ? "bg-teal-700 text-white border-teal-700" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}>
            All · {counts.all}
          </button>
          <button onClick={() => setFilter("operational")} data-testid="classification-filter-op"
                  className={`text-[11px] px-2.5 py-1 rounded border ${filter === "operational" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}>
            Operational · {counts.op}
          </button>
          <button onClick={() => setFilter("non_operational")} data-testid="classification-filter-nop"
                  className={`text-[11px] px-2.5 py-1 rounded border ${filter === "non_operational" ? "bg-zinc-700 text-white border-zinc-700" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}>
            Non-Op · {counts.nop}
          </button>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find category…"
                 className="pl-7 h-8 w-64 text-[12px]" data-testid="classification-search" />
          {q && <button onClick={() => setQ("")} className="absolute right-2 top-2 text-zinc-400"><X className="w-3.5 h-3.5" /></button>}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-[9.5px] uppercase tracking-wider text-zinc-500">
              <th className="py-2.5 px-3">Category</th>
              <th className="py-2.5 px-2 text-right">{data.month_label} total</th>
              <th className="py-2.5 px-2">Current</th>
              <th className="py-2.5 px-2">Source</th>
              <th className="py-2.5 px-2 text-right">Toggle</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan={5} className="py-8 text-center text-zinc-400">No matching categories.</td></tr>
            ) : filteredRows.map((c) => (
              <tr key={c.category} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                <td className="py-2.5 px-3 font-bold text-zinc-800">{c.category}</td>
                <td className="py-2.5 px-2 text-right tabular-nums text-zinc-700">₹{(c.company_total / 1e5).toFixed(1)}L</td>
                <td className="py-2.5 px-2">
                  <span className={`inline-block text-[10px] uppercase tracking-wider font-bold border px-2 py-0.5 rounded ${
                    c.is_operational
                      ? "text-emerald-800 bg-emerald-100 border-emerald-300"
                      : "text-zinc-700 bg-zinc-200 border-zinc-300"
                  }`}>
                    {c.is_operational ? "Operational" : "Non-Op"}
                  </span>
                </td>
                <td className="py-2.5 px-2 text-[10px] text-zinc-500 uppercase tracking-wider">{c.source}</td>
                <td className="py-2.5 px-2 text-right">
                  <div className="flex items-center gap-1.5 justify-end">
                    <button onClick={() => toggle(c.category, c.is_operational)}
                            disabled={busy === c.category}
                            data-testid={`classification-toggle-${c.category.replace(/\W/g, "")}`}
                            className="text-[10.5px] px-2 py-1 rounded border bg-white border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1">
                      {busy === c.category ? <Loader2 className="w-3 h-3 animate-spin" /> : (c.is_operational ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />)}
                      Make {c.is_operational ? "Non-Op" : "Op"}
                    </button>
                    {c.source === "runtime" && (
                      <button onClick={() => reset(c.category)}
                              disabled={busy === c.category}
                              data-testid={`classification-reset-${c.category.replace(/\W/g, "")}`}
                              className="text-[10.5px] px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Reset
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10.5px] text-zinc-500 flex items-start gap-1.5 max-w-3xl">
        <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          Defaults flagged as Non-Op: <span className="font-bold">OTA Commission · Finance Cost · F&B Expenses · Depreciation · ID & Experiences · Payment Collection</span>.
          Toggle to re-classify any head. <span className="font-bold">runtime</span> = admin-set in MongoDB · <span className="font-bold">seed</span> = built-in default Non-Op · <span className="font-bold">default</span> = treated as Operational.
        </span>
      </div>
    </div>
  );
}
